import { ArrowLongIcon } from '@common_components/icons/arrowLong.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';
import BottomBar from '@common_components/layout/bottomBar.component';
import { CommonGlobalService } from '@umanforge/common';
import { Theme } from 'interfaces/theme.interface';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EventManagerFooterProps {
  themes: Theme[],
  addTheme?: () => void;
  onDoubleClick?: ( index: number, theme: Theme ) => void;
  onSingleClick?: ( theme: Theme ) => void;
  isLeftNavBar?: boolean
  hide?: boolean
}

const EventManagerFooter: React.FC<EventManagerFooterProps> = ( { themes, addTheme, onDoubleClick, onSingleClick, isLeftNavBar = false, hide }: EventManagerFooterProps ) => {

  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>( null );
  const clickTimeout = useRef<ReturnType<typeof setTimeout> | null>( null );

  const [ isOverflowing, setIsOverflowing ] = useState( false );

  useEffect( () => {
    if ( themes.length > 0 ) {
      checkOverflow();
    }
  }, [ themes ] );

  const checkOverflow = () => {
    if ( containerRef.current ) {
      const isOverflow = containerRef.current.scrollWidth > containerRef.current.clientWidth;
      setIsOverflowing( isOverflow );
    }
  };

  const scrollLeft = () => {
    if ( containerRef.current ) {
      containerRef.current.scrollBy( {
        left: -600,
        behavior: 'smooth',
      } );
    }
  };

  const scrollRight = () => {
    if ( containerRef.current ) {
      containerRef.current.scrollBy( { left: 600, behavior: 'smooth' } );
    }
  };

  return (
    <BottomBar isLeftNavBar={ isLeftNavBar } hide={ hide }>
      <div className='relative flex h-[56px]'>
        <div
          ref={ containerRef }
          className='w-full pl-2 relative mr-4 h-full gap-x-4 flex overflow-y-hidden no-scrollbar py-1'
        >
          {/*-------- Add theme Button -----------*/}
          {
            onDoubleClick && addTheme && (
              <div className='h-full w-fit flex-center group cursor-pointer' onClick={() => {return addTheme();}}>
                <button className='px-6 p-1 gap-2 flex-center flex trans-fast shadow bg-green-200
                  text-green-800 rounded-md trans-fast group-hover:bg-green-300 w-full'
                >
                  <PlusIcon size={5} className='trans-lessfast group-hover:rotate-[360deg]'/>
                  <p className='trans-fast truncate text-sm'>{t( 'common:add_a_theme' )}</p>
                </button>
              </div>
            )
          }

          {/*-------- All themes Button -----------*/}
          {
            themes.map( ( theme, index ) => {
              const hoverTextColorClass = CommonGlobalService.getTextColor( theme.color ) === 'black' ? 'group-hover:text-black' : 'group-hover:text-white';
              return (
                <div
                  key={index}
                  className={`flex-center relative bg-white shadow-md dark:bg-dark-bg-light dark:text-white p-1 rounded-md select-none ${ onDoubleClick || onSingleClick ? 'group cursor-pointer' : '' }`}
                  onDoubleClick={onDoubleClick ? () => {
                    if ( clickTimeout.current ) { clearTimeout( clickTimeout.current ); }
                    clickTimeout.current = setTimeout( () => {
                      onDoubleClick( index, theme );
                    }, 250 );
                  } : undefined}
                  onClick={ onSingleClick ? () => {
                    if ( clickTimeout.current ) {
                      clearTimeout( clickTimeout.current );
                      clickTimeout.current = null;
                    }
                    onSingleClick( theme );
                  } : undefined }
                  style={ !onDoubleClick ? { pointerEvents: 'auto', opacity: 0.85 } : undefined }
                >
                  <div className='absolute pl-0.5 pt-0.5 left-0 w-full h-full overflow-hidden rounded-md'>
                    <div
                      className='rounded-full group-hover:scale-[50] absolute trans-lessfast w-2.5 h-2.5 mr-2 shadow-md'
                      style={{ backgroundColor: theme.color }}
                    />
                  </div>

                  <p className={`whitespace-nowrap w-auto trans-lessfast text-sm z-10 mx-3 text-black dark:text-white ${hoverTextColorClass}`}>
                    {theme.name}
                  </p>

                </div>
              );} )
          }
        </div>

        {/*-------- Scroll themes Arrow Button -----------*/}
        <div className='h-full flex-center dark:text-white text-black rounded-md'>
          {isOverflowing && (
            <>
              <button className='scroll-arrow py-1 left-0 flex-center rounded-l-md bg-white trans-fast dark:hover:bg-dark-bg-light2 dark:bg-dark-bg-light px-2 hover:bg-gray-100' onClick={scrollLeft}>
                <ArrowLongIcon className='rotate-180' />
              </button>
              <button className='scroll-arrow py-1 right-0 flex-center rounded-r-md bg-white trans-fast dark:hover:bg-dark-bg-light2 dark:bg-dark-bg-light px-2 hover:bg-gray-100' onClick={scrollRight}>
                <ArrowLongIcon />
              </button>
            </>
          )}
        </div>
      </div>
    </BottomBar>
  );
};

export default EventManagerFooter;
